import { defaultTables, type Difficulty } from "../math/questions";
import type { KeyDeck } from "../math/keys";

export type Protocol = "intercept" | "sweep";
export type Quality = "high" | "low";
export type KeyMode = "shuffle" | "lock";

export interface Settings {
  protocol: Protocol;
  tables: number[];
  difficulty: Difficulty;
  deck: KeyDeck;
  keyMode: KeyMode;
  quality: Quality;
  squaresOnly: boolean;
  muted: boolean;
}

export interface SaveData extends Settings {
  best: { intercept: number; sweep: number };
}

const KEY = "mathblaster-v1";
const OLD_SETTINGS = "nexus-factor-settings";
const OLD_BEST = "nexus-factor-best";

const DEFAULTS: SaveData = {
  protocol: "intercept",
  tables: defaultTables(),
  difficulty: "pilot",
  deck: "home",
  keyMode: "shuffle",
  quality: "high",
  squaresOnly: false,
  muted: false,
  best: { intercept: 0, sweep: 0 },
};

function asDifficulty(value: unknown): Difficulty {
  if (value === "cadet" || value === "pilot" || value === "ace") return value;
  if (value === "officer") return "pilot";
  if (value === "replicant") return "ace";
  return DEFAULTS.difficulty;
}

function asDeck(value: unknown): KeyDeck {
  return value === "mixed" || value === "full" || value === "home" ? value : DEFAULTS.deck;
}

function asTables(value: unknown): number[] {
  if (!Array.isArray(value)) return [...DEFAULTS.tables];
  const tables = value.filter((n): n is number => typeof n === "number" && n >= 1 && n <= 12);
  return tables.length ? tables : [...DEFAULTS.tables];
}

export function loadSave(): SaveData {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<SaveData> & { best?: Partial<SaveData["best"]> };
      return {
        ...DEFAULTS,
        ...parsed,
        tables: asTables(parsed.tables),
        difficulty: asDifficulty(parsed.difficulty),
        deck: asDeck(parsed.deck),
        keyMode: parsed.keyMode === "lock" ? "lock" : "shuffle",
        quality: parsed.quality === "low" ? "low" : "high",
        protocol: parsed.protocol === "sweep" ? "sweep" : "intercept",
        best: {
          intercept: Number(parsed.best?.intercept) || 0,
          sweep: Number(parsed.best?.sweep) || 0,
        },
      };
    }
  } catch {
    /* fall through to migrate */
  }
  return migrateLegacy();
}

function migrateLegacy(): SaveData {
  const save: SaveData = {
    ...DEFAULTS,
    tables: [...DEFAULTS.tables],
    best: { intercept: 0, sweep: 0 },
  };
  try {
    const best = Number(localStorage.getItem(OLD_BEST) ?? 0);
    if (Number.isFinite(best) && best > 0) save.best.intercept = best;
    const raw = localStorage.getItem(OLD_SETTINGS);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Settings>;
      save.tables = asTables(parsed.tables);
      save.difficulty = asDifficulty(parsed.difficulty);
      save.deck = asDeck(parsed.deck);
      save.squaresOnly = Boolean(parsed.squaresOnly);
    }
  } catch {
    /* keep defaults */
  }
  writeSave(save);
  return save;
}

export function writeSave(data: SaveData): void {
  localStorage.setItem(KEY, JSON.stringify(data));
}

export function rememberBest(data: SaveData, protocol: Protocol, score: number): SaveData {
  const next: SaveData = {
    ...data,
    best: {
      ...data.best,
      [protocol]: Math.max(data.best[protocol], Math.floor(score)),
    },
  };
  writeSave(next);
  return next;
}
